import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trash2, Phone, MessageCircle, Copy, RefreshCw } from "lucide-react";

const STATUS = ["new", "contacted", "converted", "lost"];
const STATUS_LABEL = { new: "Novo", contacted: "Contatado", converted: "Convertido", lost: "Perdido" };
const STATUS_COLOR = { new: "bg-accent-yellow text-black", contacted: "bg-blue-100 text-blue-800", converted: "bg-green-100 text-green-800", lost: "bg-neutral-200 text-neutral-600" };

function fmtDate(iso) {
  if (!iso) return "";
  try { const d = new Date(iso); return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ChatbotLeadsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = () => {
    setLoading(true);
    const q = statusFilter ? `?status=${statusFilter}` : "";
    api.get(`/admin/chatbot-leads${q}`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/chatbot-leads/${id}`, { status });
      setItems((arr) => arr.map((it) => it.id === id ? { ...it, status } : it));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch { toast.error("Erro ao atualizar."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este lead?")) return;
    try {
      await api.delete(`/admin/chatbot-leads/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Removido.");
    } catch { toast.error("Erro ao excluir."); }
  };

  const copyPhone = (phone) => {
    navigator.clipboard?.writeText(phone);
    toast.success("Telefone copiado.");
  };
  const openWhatsapp = (phone) => {
    const digits = (phone || "").replace(/\D/g, "");
    if (!digits) return toast.error("Telefone inválido.");
    window.open(`https://wa.me/${digits}`, "_blank");
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent-orange font-bold flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> Pré-atendimento
          </div>
          <h1 className="font-display text-4xl">Leads do Chatbot</h1>
          <p className="text-sm text-neutral-500 mt-1">Leads capturados pela Marina (balão flutuante) — cada linha tem a conversa completa.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="chatbot-leads-filter"
            className="border border-black/15 px-3 py-2 rounded text-sm bg-white">
            <option value="">Todos status</option>
            {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
          <button onClick={load} data-testid="chatbot-leads-refresh"
            className="text-sm border border-black/15 px-3 py-2 rounded-full hover:bg-black/5"><RefreshCw className="w-3 h-3 inline mr-1" /> Atualizar</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="md:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="chatbot-leads-list">
          {loading && <div className="text-sm text-neutral-500">Carregando…</div>}
          {!loading && items.length === 0 && <div className="text-sm text-neutral-500 text-center py-12 border border-dashed border-black/15 rounded">Nenhum lead ainda.</div>}
          {items.map((it) => (
            <button key={it.id} onClick={() => setSelected(it)}
              data-testid={`chatbot-lead-${it.id}`}
              className={`w-full text-left border rounded-md p-3 transition-colors ${selected?.id === it.id ? "border-black bg-black/[0.03]" : "border-black/10 hover:bg-black/[0.02]"}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-sm truncate">{it.name || "(sem nome)"}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-semibold ${STATUS_COLOR[it.status] || STATUS_COLOR.new}`}>{STATUS_LABEL[it.status] || it.status}</span>
              </div>
              <div className="text-xs text-neutral-500 font-mono">{it.phone}</div>
              <div className="text-[11px] text-neutral-400 mt-1">{fmtDate(it.created_at)}</div>
            </button>
          ))}
        </div>

        {/* Detalhe */}
        <div className="md:col-span-2">
          {!selected ? (
            <div className="border border-dashed border-black/15 rounded-md p-10 text-center text-sm text-neutral-500">
              Selecione um lead à esquerda para ver a conversa.
            </div>
          ) : (
            <div className="border border-black/10 rounded-md p-5" data-testid="chatbot-lead-detail">
              <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                <div>
                  <h2 className="font-display text-2xl">{selected.name || "(sem nome)"}</h2>
                  <div className="text-xs text-neutral-500">Recebido em {fmtDate(selected.created_at)}</div>
                  {selected.page_url && <div className="text-xs text-neutral-500 mt-1">Origem: <code className="font-mono">{selected.page_url}</code></div>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyPhone(selected.phone)} data-testid="chatbot-lead-copy" className="text-xs border border-black/15 px-3 py-1.5 rounded-full hover:bg-black/5"><Copy className="w-3 h-3 inline mr-1" /> Copiar tel</button>
                  <button onClick={() => openWhatsapp(selected.phone)} data-testid="chatbot-lead-whatsapp" className="text-xs bg-green-600 text-white px-3 py-1.5 rounded-full hover:bg-green-700"><Phone className="w-3 h-3 inline mr-1" /> WhatsApp</button>
                  <button onClick={() => remove(selected.id)} className="text-xs text-red-500 hover:text-red-700 px-2"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-3 mb-4">
                <div className="border border-black/10 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Telefone</div>
                  <div className="text-sm font-mono">{selected.phone}</div>
                </div>
                <div className="border border-black/10 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Interesse</div>
                  <div className="text-sm">{selected.interest || <em className="text-neutral-400">—</em>}</div>
                </div>
                <div className="border border-black/10 rounded p-3">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Status</div>
                  <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)}
                    data-testid="chatbot-lead-status"
                    className="w-full text-sm bg-white mt-1">
                    {STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
              </div>

              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Conversa</div>
              <div className="border border-black/10 rounded-md bg-neutral-50 p-4 space-y-2 max-h-[420px] overflow-y-auto" data-testid="chatbot-lead-conversation">
                {(selected.conversation || []).length === 0 && <div className="text-xs text-neutral-400 italic">Sem histórico de mensagens.</div>}
                {(selected.conversation || []).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-accent-yellow text-black" : "bg-white border border-black/10"}`}>
                      {m.text || m.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
